import type { TypedSupabaseClient } from '../client';
import { fetchProArticleSnapshots, type ProArticleSnapshot } from './pro-article-snapshots';
import type {
  FulfilProProductRequestInput,
  IProProductRequestRepository,
  MarkProProductRequestUnavailableInput,
  ProDevisQuoteUpdate,
  ProProductRequest,
  ProProductRequestFilters,
  ReopenProProductRequestInput,
} from '../../../interfaces/src';

/**
 * Demandes de produits hors catalogue.
 *
 * Les écritures passent par `pro_resolve_product_request` /
 * `pro_notify_product_requests` : substituer un produit touche trois tables
 * (la demande, l'en-tête du devis, ses lignes) et doit être atomique. Les
 * montants sont calculés en amont par `libs/domain/pro` — la RPC n'arbitre
 * rien.
 */
export class SupabaseProProductRequestRepository
  implements IProProductRequestRepository
{
  constructor(private supabase: TypedSupabaseClient) {}

  async list(filters?: ProProductRequestFilters): Promise<ProProductRequest[]> {
    let query = (this.supabase as any)
      .from('pro_product_requests')
      .select('*')
      .order('created_at', { ascending: false })
      .order('line_number', { ascending: true });
    if (filters?.status) query = query.eq('status', filters.status);
    if (filters?.devisCode) query = query.eq('devis_code', filters.devisCode);
    if (filters?.unnotifiedOnly) query = query.is('notified_at', null);
    const { data, error } = await query;
    if (error) throw error;
    return this.decorate((data ?? []) as Array<Record<string, unknown>>);
  }

  async listForDevis(devisCode: string): Promise<ProProductRequest[]> {
    const { data, error } = await (this.supabase as any)
      .from('pro_product_requests')
      .select('*')
      .eq('devis_code', devisCode)
      .order('line_number', { ascending: true });
    if (error) throw error;
    return this.decorate((data ?? []) as Array<Record<string, unknown>>);
  }

  async fulfil(
    id: string,
    input: FulfilProProductRequestInput,
  ): Promise<ProProductRequest> {
    return this.resolve(id, 'fulfilled', {
      articleId: input.articleId,
      quantity: input.quantity,
      note: input.note ?? null,
      quoteUpdate: input,
    });
  }

  async markUnavailable(
    id: string,
    input?: MarkProProductRequestUnavailableInput,
  ): Promise<ProProductRequest> {
    return this.resolve(id, 'unavailable', {
      note: input?.note ?? null,
      quoteUpdate: input?.quoteUpdate,
    });
  }

  async reopen(
    id: string,
    input?: ReopenProProductRequestInput,
  ): Promise<ProProductRequest> {
    return this.resolve(id, 'pending', { quoteUpdate: input?.quoteUpdate });
  }

  /**
   * Une seule RPC pour les trois décisions.
   *
   * `p_lines` n'est envoyé QUE si l'appelant a recalculé le devis : c'est le
   * cas d'une substitution (ligne ajoutée) et d'un retour en arrière sur une
   * substitution (ligne retirée). Un simple refus ne touche pas au devis, et
   * envoyer des lignes ferait échouer la RPC sur un devis déjà validé.
   */
  private async resolve(
    id: string,
    status: 'pending' | 'fulfilled' | 'unavailable',
    opts: {
      articleId?: string;
      quantity?: number;
      note?: string | null;
      quoteUpdate?: ProDevisQuoteUpdate;
    },
  ): Promise<ProProductRequest> {
    const q = opts.quoteUpdate;
    const { data, error } = await (this.supabase as any).rpc(
      'pro_resolve_product_request',
      {
        p_request_id: id,
        p_status: status,
        p_article_id: opts.articleId ?? null,
        p_resolved_quantity: opts.quantity ?? null,
        p_resolution_note: opts.note ?? null,
        p_pht: q ? q.pht : null,
        p_pttc: q ? q.pttc : null,
        p_tva: q ? q.tva : null,
        p_total_commission: q ? q.totalCommission : null,
        p_lines: q
          ? q.lines.map((l) => ({
              article_id: l.articleId,
              quantite: l.quantite,
              pht: l.pht,
              pttc: l.pttc,
              tva: l.tva,
              remise: l.remise,
              montant_remise: l.montantRemise,
            }))
          : null,
      },
    );
    if (error) throw error;
    return (await this.decorate([data as Record<string, unknown>]))[0]!;
  }

  async notify(devisCode: string): Promise<void> {
    const { error } = await (this.supabase as any).rpc(
      'pro_notify_product_requests',
      { p_devis_code: devisCode },
    );
    if (error) throw error;
  }

  /** Résout l'identité de l'article de remplacement (par id, sans plafond). */
  private async decorate(
    rows: Array<Record<string, unknown>>,
  ): Promise<ProProductRequest[]> {
    const snapshots = await fetchProArticleSnapshots(
      this.supabase,
      rows.map((r) => r['resolved_article_id'] as string | null),
    );
    return rows.map((row) => mapRequest(row, snapshots));
  }
}

function mapRequest(
  row: Record<string, unknown>,
  snapshots?: Map<string, ProArticleSnapshot>,
): ProProductRequest {
  const resolvedArticleId = (row['resolved_article_id'] as string | null) ?? null;
  const snapshot = resolvedArticleId ? snapshots?.get(resolvedArticleId) : undefined;
  return {
    id: row['id'] as string,
    tenantId: row['tenant_id'] as string,
    proCustomerId: row['pro_customer_id'] as string,
    devisCode: row['devis_code'] as string,
    lineNumber: Number(row['line_number'] ?? 0),
    label: (row['label'] as string) ?? '',
    quantity: Number(row['quantity'] ?? 0),
    note: (row['note'] as string) ?? null,
    status: row['status'] as ProProductRequest['status'],
    resolvedArticleId,
    resolvedQuantity:
      row['resolved_quantity'] == null ? null : Number(row['resolved_quantity']),
    resolutionNote: (row['resolution_note'] as string) ?? null,
    resolvedAt: (row['resolved_at'] as string) ?? null,
    resolvedBy: (row['resolved_by'] as string) ?? null,
    notifiedAt: (row['notified_at'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
    resolvedArticleName: snapshot?.name ?? null,
    resolvedArticleCode: snapshot?.code ?? null,
    resolvedArticleImageUrl: snapshot?.imageUrl ?? null,
  };
}
